import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../design/ui";
const VisualDocumentExample = lazy(() => import('../components/document/VisualDocumentExample'));

type AiClient = "claude-code" | "claude-desktop" | "chatgpt" | "gemini";
type AgentDeploy = "self-hosted" | "cloud";

const AI_CLIENTS: { id: AiClient; label: string }[] = [
  { id: "claude-code", label: "Claude Code" },
  { id: "claude-desktop", label: "Claude Desktop" },
  { id: "chatgpt", label: "ChatGPT" },
  { id: "gemini", label: "Gemini" },
];

const CODE = { color: "var(--kt-teal-ink)", fontFamily: "var(--font-mono)", fontSize: ".92em" } as const;

// Page-scoped styles for repeated blocks (section heading, feature/integration
// cards, tag chips) — kept local since these are guide-page-only compositions
// over the shared kt-* tokens, same pattern as StandupPage's SN_CSS.
const GUIDE_CSS = `
.gp-page{max-width:768px;margin:0 auto;display:flex;flex-direction:column;gap:64px;padding-bottom:80px;}
.gp-section{display:flex;flex-direction:column;gap:24px;}
.gp-section-title{font-size:24px;font-weight:700;color:var(--kt-ink);}
.gp-hero{text-align:center;padding-top:32px;display:flex;flex-direction:column;gap:16px;}
.gp-hero-title{font-size:36px;font-weight:700;color:var(--kt-ink);}
.gp-hero-sub{color:var(--kt-text-secondary);font-size:18px;max-width:576px;margin:0 auto;}
.gp-body{color:var(--kt-text-secondary);display:flex;flex-direction:column;gap:12px;}
.gp-heading3{color:var(--kt-ink);font-size:18px;font-weight:600;}
.gp-card-title{color:var(--kt-ink);font-weight:600;margin-bottom:4px;}
.gp-card-desc{color:var(--kt-text-secondary);font-size:14px;}
.gp-tag{font-size:12px;background:var(--kt-teal-tint);color:var(--kt-teal-ink);padding:2px 8px;border-radius:8px;}
.gp-strong{color:var(--kt-ink);}
`;

function CodeBlock({ children }: { children: string }) {
  return (
    <pre
      className="kt-card"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        color: "var(--kt-ink)",
        background: "var(--kt-strip-bg)",
        overflowX: "auto",
        whiteSpace: "pre-wrap",
      }}
    >
      {children}
    </pre>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 20 }}>
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "var(--kt-teal)",
          color: "var(--kt-on-teal)",
          fontSize: 14,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
        }}
      >
        {number}
      </div>
      <div style={{ flex: 1 }}>
        <h3 className="gp-heading3" style={{ marginBottom: 8 }}>{title}</h3>
        <div className="gp-body">{children}</div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="kt-card">
      <div style={{ marginBottom: 12 }}><Icon name={icon} size={22} /></div>
      <h3 className="gp-card-title">{title}</h3>
      <p className="gp-card-desc">{description}</p>
    </div>
  );
}

function AgentSetupPrompts() {
  const mcpUrl = import.meta.env.VITE_MCP_URL;
  return (
    <div className="kt-card" style={{ background: "var(--kt-strip-bg)", display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
      <p style={{ color: "var(--kt-ink)", fontWeight: 500, fontSize: 14 }}>Shortcut — let a coding agent set it up</p>
      <p style={{ fontSize: 12, color: "var(--kt-text-secondary)" }}>
        Coding agents (Claude Code, Cursor, Windsurf) can discover and configure Aeronet Brain
        automatically. Just paste this prompt:
      </p>
      <CodeBlock>{`Set up my Aeronet Brain MCP server at ${mcpUrl}, then add it to my MCP client config as "open-brain". OAuth authentication will happen automatically the first time you use a brain tool.`}</CodeBlock>
      <p style={{ fontSize: 12, color: "var(--kt-text-secondary)" }}>
        Or for a regular AI agent that doesn't manage config files:
      </p>
      <CodeBlock>{`Connect to my personal knowledge base at ${mcpUrl} and authenticate via OAuth. Once connected, use the brain tools to search, capture, and browse my thoughts.`}</CodeBlock>
    </div>
  );
}

function AiClientSetup({ client }: { client: AiClient }) {
  const mcpUrl = import.meta.env.VITE_MCP_URL;

  if (client === "claude-code") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p>Add Aeronet Brain as an MCP server in your Claude Code settings:</p>
        <p>Add this to <code style={CODE}>~/.claude/settings.json</code>:</p>
        <CodeBlock>{`{
  "mcpServers": {
    "open-brain": {
      "type": "http",
      "url": "${mcpUrl}"
    }
  }
}`}</CodeBlock>
        <p>Claude Code will authenticate automatically via OAuth the first time you use a brain tool. You can then say things like:</p>
        <ul style={{ listStyle: "disc", paddingLeft: 24, fontSize: 14 }}>
          <li>"Remember this decision"</li>
          <li>"What do I know about [topic]?"</li>
          <li>"Show me my recent thoughts"</li>
        </ul>
      </div>
    );
  }

  if (client === "claude-desktop") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p>Open Claude Desktop and go to <strong className="gp-strong">Settings → MCP Connectors → Add connector</strong>. Find your MCP URL in <strong className="gp-strong">Settings → MCP Connection</strong> after signing in.</p>
        <CodeBlock>{`Name:   Aeronet Brain
URL:    ${mcpUrl}
Auth:   OAuth (automatic)`}</CodeBlock>
        <p>Then add the skill instructions so Claude knows when to use the brain. Copy the contents of <code style={CODE}>skills/claude-desktop.md</code> from the repo into <strong className="gp-strong">Settings → Project Instructions</strong>.</p>
        <p>After connecting, try:</p>
        <ul style={{ listStyle: "disc", paddingLeft: 24, fontSize: 14 }}>
          <li>"What did I decide about X?" — Claude will search your brain</li>
          <li>"Remember that I prefer Y" — Claude will save it</li>
        </ul>
      </div>
    );
  }

  if (client === "chatgpt") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p>ChatGPT requires a paid plan with MCP support enabled.</p>
        <ol style={{ listStyle: "decimal", paddingLeft: 24, fontSize: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <li>Go to <strong className="gp-strong">Settings → Apps &amp; Connectors → Advanced settings</strong> and enable Developer Mode</li>
          <li>Add a new MCP connector with your MCP URL from <strong className="gp-strong">Settings → MCP Connection</strong></li>
          <li>Go to <strong className="gp-strong">Settings → Personalization → Custom Instructions</strong> and paste the contents of <code style={CODE}>skills/chatgpt-instructions.md</code></li>
        </ol>
        <p
          className="kt-card"
          style={{ fontSize: 14, color: "var(--kt-ink)", background: "var(--kt-strip-bg)" }}
        >
          Note: ChatGPT requires explicit tool names in instructions. The skill file is already formatted for this.
        </p>
      </div>
    );
  }

  if (client === "gemini") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p><strong className="gp-strong">Gemini CLI</strong> — connect via the MCP add command. Create an agent API key from the web dashboard (the <strong className="gp-strong">Agents</strong> page), then use it here. Find your MCP URL in <strong className="gp-strong">Settings → MCP Connection</strong>.</p>
        <CodeBlock>{`gemini mcp add -t http open-brain \\
  ${mcpUrl} \\
  -H "X-Api-Key: YOUR_API_KEY"`}</CodeBlock>
        <p>Then add the skill to <code style={CODE}>~/.gemini/GEMINI.md</code> — copy the CLI section from <code style={CODE}>skills/gemini-gem.md</code>.</p>
        <div style={{ borderTop: "1px solid var(--kt-border)", paddingTop: 16 }}>
          <p><strong className="gp-strong">Gemini Web</strong> — MCP connectors are not yet supported in Gemini web. You can still use Aeronet Brain by:</p>
          <ul style={{ listStyle: "disc", paddingLeft: 24, fontSize: 14, marginTop: 8 }}>
            <li>Capturing through Claude Code or Claude Desktop, then asking Gemini about topics (once MCP ships)</li>
            <li>Creating a Gem with the instructions from <code style={CODE}>skills/gemini-gem.md</code> for manual capture guidance</li>
          </ul>
        </div>
      </div>
    );
  }

  return null;
}

function AgentDeployTabs() {
  const [tab, setTab] = useState<AgentDeploy>("self-hosted");
  const mcpUrl = import.meta.env.VITE_MCP_URL;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div role="tablist" style={{ display: "flex", gap: 8 }}>
        {([["self-hosted", "Self-hosted (Mac Mini)"], ["cloud", "GitHub Actions (Cloud)"]] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={tab === id ? "kt-btn kt-btn--primary" : "kt-btn"}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="kt-card" style={{ fontSize: 14, color: "var(--kt-text-secondary)", display: "flex", flexDirection: "column", gap: 16 }}>
        {tab === "self-hosted" && (
          <>
            <p>Run agents on a <strong className="gp-strong">self-hosted GitHub Actions runner</strong> — ideal if you have a Mac Mini or always-on machine. Agents get full tool access, can persist state to disk between runs, and can run as long-running launchd services.</p>
            <CodeBlock>{`# .github/workflows/my-agent.yml
on:
  schedule:
    - cron: "30 11 * * *"  # 6:30am EST daily
  workflow_dispatch:

jobs:
  run:
    runs-on: self-hosted
    steps:
      - uses: actions/checkout@v5
      - uses: actions/setup-node@v5
        with: { node-version: "22" }
      - run: npm ci
      - run: npm run my-agent
        env:
          ANTHROPIC_API_KEY: \${{ secrets.ANTHROPIC_API_KEY }}
          OPEN_BRAIN_URL: \${{ secrets.OPEN_BRAIN_URL }}
          OPEN_BRAIN_KEY: \${{ secrets.OPEN_BRAIN_KEY }}`}</CodeBlock>
            <p style={{ fontSize: 12 }}>Set <code style={CODE}>OPEN_BRAIN_URL</code> to <code style={CODE}>{mcpUrl?.replace("/mcp", "") ?? "https://brain.blanxlait.ai"}</code> and <code style={CODE}>OPEN_BRAIN_KEY</code> to the key you just created — both as GitHub repo secrets.</p>
          </>
        )}
        {tab === "cloud" && (
          <>
            <p>Run agents on <strong className="gp-strong">GitHub-hosted runners</strong> — no hardware required. Ideal for simple batch agents. Stateless: no local file persistence between runs. GitHub Copilot users get included Actions minutes.</p>
            <CodeBlock>{`# .github/workflows/my-agent.yml
on:
  schedule:
    - cron: "30 11 * * *"  # 6:30am EST daily
  workflow_dispatch:

jobs:
  run:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v5
      - uses: actions/setup-node@v5
        with: { node-version: "22" }
      - run: npm ci
      - run: npm run my-agent
        env:
          ANTHROPIC_API_KEY: \${{ secrets.ANTHROPIC_API_KEY }}
          OPEN_BRAIN_URL: \${{ secrets.OPEN_BRAIN_URL }}
          OPEN_BRAIN_KEY: \${{ secrets.OPEN_BRAIN_KEY }}`}</CodeBlock>
            <p style={{ fontSize: 12 }}>Same secrets as self-hosted. The only difference is <code style={CODE}>runs-on: ubuntu-latest</code> — GitHub provisions a fresh VM for each run.</p>
          </>
        )}
      </div>
    </div>
  );
}

function IntegrationHeading({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icon name={icon} size={22} />
      <h3 className="gp-heading3">{title}</h3>
    </div>
  );
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return <span className="gp-tag">{children}</span>;
}

export function GuidePage() {
  const [activeClient, setActiveClient] = useState<AiClient>("claude-code");

  return (
    <>
      <style>{GUIDE_CSS}</style>
      <div className="gp-page">
        {/* Hero */}
        <div className="gp-hero">
          <h1 className="gp-hero-title">Your second brain, powered by AI</h1>
          <p className="gp-hero-sub">
            Aeronet Brain stores your thoughts, decisions, and knowledge as searchable memories — accessible from any AI assistant you already use.
          </p>
          <div style={{ paddingTop: 8 }}>
            <Link to="/login" className="kt-btn kt-btn--primary" style={{ textDecoration: "none", display: "inline-block" }}>
              Get started
            </Link>
          </div>
        </div>

        {/* Getting Started */}
        <div id="how-it-works" className="gp-section">
          <h2 className="gp-section-title">Getting started</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <Step number={1} title="Sign in">
              <p>
                Click <strong className="gp-strong">Get started</strong> and sign in with your Aeronet SSO account (the same login as the CRM). Once an admin has added you on the Users page, your access is ready on first sign-in.
              </p>
            </Step>

            <Step number={2} title="Connect your AI client">
              <p>Aeronet Brain works with the AI tools you already use. Pick yours:</p>
              <AgentSetupPrompts />
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                <div role="tablist" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {AI_CLIENTS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      role="tab"
                      aria-selected={activeClient === c.id}
                      onClick={() => setActiveClient(c.id)}
                      className={activeClient === c.id ? "kt-btn kt-btn--primary" : "kt-btn"}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="kt-card" style={{ fontSize: 14 }}>
                  <AiClientSetup client={activeClient} />
                </div>
              </div>
            </Step>

            <Step number={3} title="Capture your first thought">
              <p>
                From the dashboard, switch to <strong className="gp-strong">Capture mode</strong> and type anything — a decision you made, something you want to remember, a project note. Or just tell your AI assistant:
              </p>
              <CodeBlock>{"Remember that I prefer TypeScript over JavaScript for new projects."}</CodeBlock>
              <p>Your AI will call <code style={CODE}>capture_thought</code> and it'll be stored with semantic embeddings, ready to recall later.</p>
            </Step>
          </div>
        </div>

        {/* Core Features */}
        <div className="gp-section">
          <h2 className="gp-section-title">What you can do</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            <FeatureCard
              icon="star"
              title="Your Today workspace"
              description="See your assigned Jira work across boards, grouped by Brain projects. My projects shows your responsibilities, favourites and projects with assigned Jira work; switch to All projects to browse the catalogue. Star your current projects or drag them from Other projects into favourites, then arrange their order. All work starts with collapsed project groups: expand a group to inspect its issues, or open its project board to manage work through the available Jira transitions. Unmatched issues stay visible under Needs project mapping. Counts show partial coverage while more Jira pages are loading. Completed work covers issues updated in the last 14 days."
            />
            <FeatureCard
              icon="messages"
              title="Correspondence & the Desk"
              description="Every record — projects, customers, visits, people, library docs, standups, plans — carries its own correspondence: comments and system events (created, went stale, Jira status, review requested) interleave into one timeline in the right-hand dock's Correspondence tab, which lights up when the record you're viewing has activity. Each top-level comment is a thread that moves Open → Waiting → Resolved on its own. The Desk (left nav) is your triage queue: every thread you follow across all records, in lanes, with mute + resolve. The top-bar bell is the quick peek; mentions and review requests also bridge to the shared Slack channel. Comments are ingested into the shared brain; notifications and system events are not."
            />
            <FeatureCard
              icon="search"
              title="Search by meaning"
              description={'Ask questions in plain English. Results are ranked by semantic similarity, not keyword matches — so "what do I know about the auth system?" finds relevant thoughts even if you never used those exact words.'}
            />
            <FeatureCard
              icon="pencil"
              title="Capture from anywhere"
              description="Save thoughts from the web dashboard, iOS app, any connected AI chat, or Slack. Everything lands in the same searchable brain."
            />
            <FeatureCard
              icon="tag"
              title="6 thought types"
              description="Thoughts are automatically classified as observation, task, idea, reference, person_note, or workflow. Browse and filter by type to find exactly what you need."
            />
            <FeatureCard
              icon="lock"
              title="Private & shared scope"
              description="Every thought is private by default. Optionally share thoughts to the public feed — useful for team knowledge or agent coordination."
            />
            <FeatureCard
              icon="calendar"
              title="Browse recent"
              description="See your thoughts in chronological order. Filter by type or topic to review what's been on your mind this week."
            />
            <FeatureCard
              icon="chart-bar"
              title="Brain stats"
              description="See a snapshot of your brain: total thoughts, breakdown by type, top topics, and people mentioned across your notes."
            />
            <FeatureCard
              icon="id"
              title="Customer & project workspaces"
              description="Every project moves through a gated status pipeline (Draft → Planning → In progress → With client → Done). Overview pins Next session pickup (open followups + 🟢/🟡/🔴) and a nextAction summary with disclosure for long prose. Tabs: Plan, Actions (action register — a per-workstream table in the call-report shape (Ref · Action · Type · Owner · Pri · Jira · Notes), plus a kanban with a group-by toggle (client status | owner) and drag-to-move, raise-to-Jira, Verify sign-off ✓, and per-action history in the edit dialog), Jira, Decisions, Files, Activity (7 ActivityType filters). Customer tabs add Engagement (cross-project register grouped by &lt;customer&gt;-crm workStreams) and Log (monthly WorkLog invoice shape)."
            />
            <FeatureCard
              icon="clipboard-list"
              title="Engagement actions & follow-ups"
              description="MCP: add_action / update_action (W1 product-aligned fields + chargeable/links/target*; plus the call-report set — category (fr | support | support-bug | support-fr), needsTicket + raiseTarget to flag &quot;needs a ticket, here&quot; WITHOUT creating one, and notes; create/update fans source:action system messages), attach_file (one-off file attach to a customer/project, optionally linked to an action as links[{type:'file'}] — same versioned scope-enforced write as check_in, without the checkout lease), add_followup / resolve_followup, log_activity (+ WorkLog when billable), get_board (read-only PI board), upsert_project board fields with wrap:true §6.1② identity refusals. escalate_action_to_jira is human-only — reuses RAISE_TARGETS, applies ws-* additively, writes jiraIssueKey, refuses if already set. Secrets in text are rejected, not redacted."
            />
            <FeatureCard
              icon="clipboard-check"
              title="Plans, reviews & decisions"
              description="One canonical plan.md per project, versioned in S3 — started from a template, a promoted file, or the start_plan MCP tool (local AI can draft the plan and land v1 in the Plan tab; it also adopts orphaned plan.md files). Edits are proposals: click a section's pencil (or Edit for the whole doc), then the Suggest-change dialog captures a summary, the why, the feedback threads it addresses, and the decisions it makes. Sign-off is the ceremony: it bumps the version, auto-resolves addressed threads (with keep-open opt-outs), locks in the carried decisions, and counts as the review approval for the current version. Review requests land in each reviewer's Desk as a waiting-on-you thread; plan discussion shares the project's one correspondence timeline."
            />
            <div id="visual-documents">
              <FeatureCard icon="chart-dots" title="Visual plans & docs" description="Markdown plans and docs can include diagrams, interactive project maps and linked resource cards. Select graph nodes for details, follow links to Jira or supporting docs, and expand a visual when you need more room. Ask your agent to add visuals to the document; its existing versions, feedback and review process still apply."/>
              <Suspense fallback={null}><VisualDocumentExample/></Suspense>
            </div>
            <FeatureCard
              icon="plug"
              title="Customer integrations"
              description="Every customer has an Integrations tab (visits-style list→detail) backed by Aurora records + docs under customers/<id>/integrations/<key>/{docs|code|runbooks|sops}/. Templates at /admin/integration-templates (dry-run test). Weekly scanner + Rescan; MCP get/update/put/rescan/verify. Silence never means none."
            />
            <FeatureCard
              icon="map-pin"
              title="Site visits"
              description="Every customer has a Site Visits tab for the call-report flow: create a visit, Add transcript / drop a file / Meet or Gemini Doc URL (or record in-room). The visit page is four tabs — Overview (stats, summary, notes for next visit, sources, tickets, reports), Discussion (accepted items + carry-over), Actions (read-only Jira mirror), and Files (uploads / transcripts / reports). AI extraction lands as proposed suggestions; the teal banner opens an inline review deck (→ accept, ← dismiss, e edit). Accepting an action raises or links a Jira issue — Jira is the sole source of truth for actions. Export freezes HTML + PDF; Mark audited publishes to the brain."
            />
            <FeatureCard
              icon="file-text"
              title="Site visits — transcripts & call reports"
              description="Three import sources feed one confirm modal (never silent create): .txt/.md/.vtt, a Google Meet link, or a Google Doc Gemini-notes export. Pipeline strip: Uploaded → Transcribing → Extracting → Ready to review. Review accepts discussion items into the record and raises/links actions to Jira. Disposition vocab includes Raise FR and Road map FR; status includes Investigate / With client / Needs Discussion. Locally, /site-visit check_out → fill visit.json → check_in still works — then Export report / Mark audited in the UI. Do not hand-craft HTML."
            />
            <FeatureCard
              icon="ticket"
              title="Customer tickets"
              description="Every customer also has a Tickets tab: live counts of open, in-progress, and recently-done Jira/JSM tickets, a filterable table linking straight out to each ticket, and the same view surfaced right on the Site Visits call report so open tickets are visible while you're in the room. Needs the customer's Jira Service Management organization mapped first — until then the tab explains what's missing instead of showing an error."
            />
            <FeatureCard
              icon="report-money"
              title="Finance (admins + finance dept)"
              description="AWS spend by customer from a nightly Cost Explorer snapshot (Account tag). The dashboard has Review and Monitor tabs above shared KPIs: Review shows cost, server size, backup size, and an editable Data Tier (small/medium/large) for manual sizing; Apply suggested fills unset tiers from usage. Monitor stacks bill split, the 13-month fleet chart, margin (pre-FX), movers, and fleet-by-service. Refresh pulls Cost Explorer asynchronously. Per-customer Finance tabs and printable pricing sheets remain on the customer page."
            />
            <FeatureCard
              icon="galaxy"
              title="Atlas"
              description="See the whole brain as a living map: every atom plotted by embedding similarity on a canvas, constellations of customers and projects pulling their knowledge together, and a replayable timeline of how the corpus grew."
            />
            <FeatureCard
              icon="device-mobile"
              title="iOS app"
              description="Search, capture, browse, and view stats from your iPhone. Full feature parity with the web dashboard — your brain in your pocket."
            />
            <FeatureCard
              icon="sparkles"
              title="Agent widget"
              description="A floating AI partner on every page that knows which record and tab you're looking at — and, on instrumented pages, a snapshot of what's actually on screen (the visible tickets, projects, counts) so 'this customer' and 'these tickets' just work. Ask about the thing in front of you and it answers from both structured records (customers, projects, tickets, site visits, and — for finance/admins — AWS spend) and the brain's thoughts, citing every claim with a chip that links to the source. It can also take you there: replies include ✦ 'Take me there' buttons that navigate anywhere in the app — straight into a specific site-visit report, a customer, a project, or any page — without opening a new tab, and the target flashes on arrival. Answers render as rich formatted text with entity summary cards, and the live trace shows what it's searching. Drag it anywhere, minimise it to the launcher, and your conversation survives a refresh."
            />
            <FeatureCard
              icon="bookmark"
              title="Save a conversation to the brain"
              description="The bookmark button asks the agent to distill the conversation into a few durable thoughts. You get editable proposal cards — tweak the text, pick a type and scope — and save each with one click. The agent proposes; you decide what's written."
            />
            <FeatureCard
              icon="link"
              title="Point at anything"
              description="With the widget open, ⌥-click (Option-click) a ticket to attach it as a reference pill on your next message — the agent anchors its answer on exactly that item. Turn on the ✦ Proactive toggle and it offers one useful line whenever you open a new record."
            />
            <FeatureCard
              icon="sparkles"
              title="Hover to Explain any figure"
              description="On the standardized customer pages, hover any highlighted figure — completeness, atoms, tickets, open work, AWS cost, a visit's open items — for ~half a second and a ✦ Explain chip appears. Click it and the widget opens pre-seeded with exactly that number, answering why it is what it is with citations (using the stronger model)."
            />
            <FeatureCard
              icon="sparkles"
              title="Draft a ticket reply"
              description="On a customer's Tickets tab, ✦ Draft answer opens the widget in draft mode: it searches similar resolved tickets fleet-wide, checks the customer's install, and produces a cited draft reply with a confidence note. Hit Copy reply to paste it into Jira — the agent never posts on its own."
            />
          </div>
        </div>

        {/* Company skills registry */}
        <div className="gp-section">
          <h2 className="gp-section-title">Company skills</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <FeatureCard
              icon="hex"
              title="Skills tab (Aeronet → Skills)"
              description="A live view of the company skills registry: skills merged to the mod-skills GitLab repo publish to the Bedrock AgentCore registry and mirror here automatically. Browse each skill's SKILL.md and companion files, copy the content, or copy a one-line install command for your ~/.claude/skills. Contribute via merge request on mod-skills."
            />
          </div>
        </div>

        {/* Integrations */}
        <div className="gp-section">
          <h2 className="gp-section-title">Integrations</h2>
          <p style={{ color: "var(--kt-text-secondary)" }}>Connect your tools and Aeronet Brain captures activity automatically — no manual entry needed.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <IntegrationHeading icon="brand-slack" title="Slack" />
              <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
                Connect your Slack workspace at <strong className="gp-strong">Settings → Connect Slack</strong>. Once connected:
              </p>
              <ul style={{ color: "var(--kt-text-secondary)", fontSize: 14, listStyle: "disc", paddingLeft: 24 }}>
                <li>DM the Aeronet Brain bot to search or capture thoughts directly from Slack</li>
                <li><code style={CODE}>/add-note &lt;text&gt;</code> — add a personal note to raise at standup</li>
                <li><code style={CODE}>/brain add-note &lt;text&gt;</code> — same, from the existing Brain command</li>
                <li>DM <code style={CODE}>note: &lt;text&gt;</code> — same, inside your Aeronet Brain bot DM</li>
                <li><code style={CODE}>/brain ask &lt;question&gt;</code> — search everything you can read</li>
                <li><code style={CODE}>/brain remember &lt;thought&gt;</code> — save to your personal brain</li>
                <li><code style={CODE}>/brain team &lt;dept&gt; &lt;thought&gt;</code> / <code style={CODE}>company &lt;thought&gt;</code> — save to a shared scope</li>
                <li><code style={CODE}>/brain tell @teammate &lt;thought&gt;</code> — propose a memory into their review queue</li>
              </ul>
              <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>Replies are private (ephemeral) to you, so tiered memories never land in a public channel.</p>
            </div>

            {/* Gmail */}
            <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <IntegrationHeading icon="mail" title="Gmail" />
              <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
                Connect Gmail at <strong className="gp-strong">Settings → Connect Gmail</strong>. Aeronet Brain pulls intentionally — only the emails worth remembering, on demand. Ask things like <em>"what did Sarah send me about the Q2 budget?"</em> and find it instantly.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 14 }}>
                <div style={{ background: "var(--kt-strip-bg)", border: "1px solid var(--kt-border)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <p style={{ color: "var(--kt-ink)", fontWeight: 500 }}>What gets captured</p>
                  <ul style={{ color: "var(--kt-text-secondary)", display: "flex", flexDirection: "column", gap: 4 }}>
                    <li>✓ 1:1 conversations</li>
                    <li>✓ Small group threads (≤6 people)</li>
                    <li>✓ Travel confirmations &amp; bookings</li>
                    <li>✓ Receipts &amp; invoices</li>
                    <li>✓ Sender, subject, date, thread ID</li>
                  </ul>
                </div>
                <div style={{ background: "var(--kt-strip-bg)", border: "1px solid var(--kt-border)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <p style={{ color: "var(--kt-danger-ink)", fontWeight: 500 }}>What's excluded</p>
                  <ul style={{ color: "var(--kt-text-secondary)", display: "flex", flexDirection: "column", gap: 4 }}>
                    <li>✗ Promotions &amp; newsletters</li>
                    <li>✗ Social notifications</li>
                    <li>✗ Automated updates &amp; alerts</li>
                    <li>✗ Large group / mailing lists</li>
                    <li>✗ Email body (never stored)</li>
                  </ul>
                </div>
              </div>

              <p style={{ color: "var(--kt-text-muted)", fontSize: 12 }}>
                Uses Gmail metadata scope only — Aeronet Brain never reads or stores email content.
              </p>
            </div>
          </div>
        </div>

        {/* Agents */}
        <div className="gp-section">
          <h2 className="gp-section-title">Agents</h2>
          <p style={{ color: "var(--kt-text-secondary)" }}>
            Agents are headless, scheduled processes — not interactive AI clients. They run unattended on a cron schedule, search and capture thoughts programmatically, and don't go through OAuth. Each agent gets its own API key.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <Step number={1} title="Create an agent key">
              <p>Go to <Link to="/agents" style={{ color: "var(--kt-teal-ink)" }}>Agents</Link> in the dashboard, click <strong className="gp-strong">New agent</strong>, and give it a name (e.g. <code style={CODE}>research-agent</code>). Copy the key — it won't be shown again.</p>
              <p style={{ fontSize: 12 }}>Use a separate key per agent so you can revoke or rotate them independently.</p>
            </Step>

            <Step number={2} title="Wire it into your agent code">
              <p>Pass the key as an MCP server header in your agent. Using the <a href="https://www.npmjs.com/package/@anthropic-ai/claude-agent-sdk" style={{ color: "var(--kt-teal-ink)" }} target="_blank" rel="noreferrer">Claude Agent SDK</a>:</p>
              <CodeBlock>{`import { query } from "@anthropic-ai/claude-agent-sdk";

for await (const msg of query({
  prompt: "Your task here...",
  options: {
    allowedTools: [
      "WebSearch", "WebFetch", "Write",
      "mcp__aeronet-brain__search_thoughts",
      "mcp__aeronet-brain__capture_thought",
      "mcp__aeronet-brain__propose_memory",
      "mcp__aeronet-brain__get_taxonomy",
      "mcp__aeronet-brain__mark_useful",
    ],
    mcpServers: {
      "open-brain": {
        type: "http",
        url: process.env.OPEN_BRAIN_URL!,
        headers: { "x-api-key": process.env.OPEN_BRAIN_KEY! },
      },
    },
    permissionMode: "bypassPermissions",
    maxTurns: 30,
    maxBudgetUsd: 1.0,
  },
})) {
  if ("result" in msg) console.log(msg.result);
}`}</CodeBlock>
            </Step>

            <Step number={3} title="Deploy on a schedule">
              <AgentDeployTabs />
            </Step>
          </div>
        </div>

        {/* Review & governance */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="news" size={22} />
            <h3 className="gp-heading3">Review &amp; governance</h3>
            <SectionTag>Dashboard</SectionTag>
            <SectionTag>Claude Code</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Team and AI-proposed memories land in a <strong className="gp-strong">pending queue</strong> instead of publishing
            straight to a shared scope. Clear it from the <strong className="gp-strong">Review</strong> tab, or via
            <code style={CODE}> review_pending</code> → <code style={CODE}>approve_memory</code> /
            <code style={CODE}>reject_memory</code>. Agents propose with
            <code style={CODE}> propose_memory</code>; humans approve. Start your day with
            <code style={CODE}> morning_brief</code>.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Captures are tagged against a closed taxonomy (<code style={CODE}>get_taxonomy</code>), so search
            accepts structured <code style={CODE}>filters</code> (type, service, project, customer, date) and an
            <code style={CODE}> auto_filter</code> mode that infers them from a natural-language query. Sensitive
            material is gated at capture: secrets/credentials are refused, personnel-adjacent notes are kept private.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Exact identifiers — Jira keys (<code style={CODE}>DO-2166</code>), error codes, versions — are indexed
            for exact match and fused with semantic results, so looking up a ticket or error code finds the right memory even when
            the wording differs. A reranking pass then re-orders the top candidates by relevance before they reach the model.
          </p>
        </div>

        {/* Daily audit & standup */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="brain" size={22} />
            <h3 className="gp-heading3">Daily audit &amp; standup</h3>
            <SectionTag>Slack</SectionTag>
            <SectionTag>Claude Code</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            A nightly audit keeps the shared brain healthy: it merges near-duplicates, deprecates expired facts, and asks
            <strong className="gp-strong"> Haiku</strong> to flag contradictions — all reversible. What it can&apos;t resolve on its
            own becomes a <strong className="gp-strong">question</strong>. Each morning a brief lands in Slack (and via
            <code style={CODE}> standup_brief</code>) with the open questions, reviews waiting &gt;48h, and a health line.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Pull standups back out from Claude Code: <code style={CODE}>get_standup</code> returns a day&apos;s summary
            (the team digest + everyone&apos;s focuses, or <code style={CODE}>view:&quot;me&quot;</code> for just your page and
            notes to raise), <code style={CODE}>get_standup_transcript</code> returns the full recorded meeting transcript
            for a day, and <code style={CODE}>list_standups</code> browses past standups newest-first.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Resolve a question with <code style={CODE}>answer_question</code> (your answer is captured as a thought
            and linked; for a contradiction, the wrong fact is retired) or <code style={CODE}>dismiss_question</code>.
            Caps keep it calm: ≤3 new questions/day, ≤10 open at once.
          </p>
        </div>

        {/* Ticket Watch */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="calendar-exclamation" size={22} />
            <h3 className="gp-heading3">Ticket Watch — deadlines, overnight digest, draft answers</h3>
            <SectionTag>Standup</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            The nightly audit also reads every open support ticket. It extracts
            <strong className="gp-strong"> respond-by deadlines from the ticket text itself</strong> (a &quot;please respond
            by June 24&quot; buried in an email is invisible to SLA fields), summarises what arrived overnight, and — where the
            brain knows enough — drafts a suggested answer.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            It also checks <strong className="gp-strong">who has the last word</strong> on every ticket: if the customer
            spoke last (or was never replied to), the ticket lands in a pinned
            <strong className="gp-strong"> Needs reply</strong> section, longest wait first, red once they&apos;ve waited
            3+ days — regardless of ticket status, so nothing falls through the net. A ranked
            <strong className="gp-strong"> Today&apos;s priorities</strong> brief says which tickets to get to first and
            whether each needs a reply or an action.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Everything surfaces on the standup <strong className="gp-strong">Tickets tab</strong>: a pinned
            <strong className="gp-strong"> Date commitments</strong> section (a hard deadline is never hidden, whatever the
            ticket&apos;s status), an <strong className="gp-strong">Overnight</strong> digest card, and an
            <strong className="gp-strong"> AI draft</strong> block on each ticket row with a Copy button. Drafts are for
            human review only — nothing is ever sent to a customer automatically, and the brain never writes to Jira.
            The audit runs nightly at ~3am NZ; <strong className="gp-strong">Run audit</strong> on the tab re-runs it on
            demand (results land a few minutes later).
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Every open ticket is also <strong className="gp-strong">scored for priority</strong> by a transparent,
            tunable engine (ported from Jay&apos;s support-triage tool): points for no-first-response, breached/approaching
            SLAs, being stuck or stale, dev-hand-off anomalies, age and Jira priority — dampened for tickets legitimately
            parked on the customer. Each ticket shows a <strong className="gp-strong">P1/P2/P3 score badge</strong>, expands
            to <strong className="gp-strong">Why this priority</strong> (the exact signals that fired), and the tab leads
            with P1/P2/P3 counts plus an <strong className="gp-strong">All open</strong> sweep sorted worst-first — expand a
            row to mark it reviewed for the day. Admins get a <strong className="gp-strong">Scoring</strong> button to retune
            every weight and the bucket thresholds live — no redeploy.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            The shared Tickets tab shows <strong className="gp-strong">customer requests only</strong> — the internal
            Task/Bug/Epic issues that also live in the Support project are filtered out there (tune via
            <code>STANDUP_EXCLUDE_ISSUE_TYPES</code>). Those internal issues still appear on the
            assignee&apos;s own standup tab, whose <strong className="gp-strong">Tickets</strong> section leads with the
            person&apos;s <strong className="gp-strong">3 most important</strong> items as cards (by score), a
            <strong className="gp-strong"> See more</strong> for the rest, then <strong className="gp-strong">Blocked</strong>
            and <strong className="gp-strong">In progress</strong> groups.
          </p>
        </div>

        {/* Run standup mode */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="player-play" size={22} />
            <h3 className="gp-heading3">Run standup — the walkthrough mode</h3>
            <SectionTag>Standup</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            <strong className="gp-strong">Run standup</strong> on the Standup page enters a full-screen,
            keyboard-driven walkthrough: people in <strong className="gp-strong">random order</strong>, each starting
            with a <strong className="gp-strong">yesterday review</strong> (tick what got done, pull anything back into
            today) followed by one card per project, ordered by recent activity. Each card shows what the system
            detected since yesterday (Jira moves, new blockers), the project&apos;s next action, and a one-tap
            intent — <strong className="gp-strong">Active today · Parked · Blocked · Handing over</strong> (keys 1–4).
            Active projects take a typed or dictated focus (suggestion chips prefill it) that lands on tomorrow&apos;s
            standup. <strong className="gp-strong">Park that</strong> (P) queues any mid-conversation moment; the queue
            is resolved in one pass at the end (→ action, → standup note, or drop), and a
            <strong className="gp-strong"> standup receipt</strong> summarises the run. The agent dock rides along as a
            full-height column with the current person + project as context. Space advances; Esc ends.
          </p>
        </div>

        {/* Focuses ritual */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="checklist" size={22} />
            <h3 className="gp-heading3">Focuses — carry-over so nothing is dropped</h3>
            <SectionTag>Standup</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Each person&apos;s tab has a <strong className="gp-strong">Today</strong> focus checklist and a
            <strong className="gp-strong"> Yesterday</strong> column showing yesterday&apos;s focuses as tickable boxes —
            tick off what got done. Anything left undone keeps resurfacing under
            <strong className="gp-strong"> Carried over</strong> with a &quot;N days ago&quot; chip, day after day, so stale
            tasks get louder rather than forgotten. Still working on one? Hit the <strong className="gp-strong">←</strong>
            button to bring it back into today&apos;s focuses (it moves, it doesn&apos;t duplicate). Ticks land on the
            focus&apos;s original day, so a closed standup still records completions.
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Link a focus to a <strong className="gp-strong">project</strong> (and optionally an issue) from the composer —
            quick-pick chips, autocomplete, and a soft name-match suggestion. Unlinked focuses show a quiet amber
            dot (never a blocker). Linked focuses light <strong className="gp-strong">Active today</strong> on the
            project Overview. Below the focuses, <strong className="gp-strong">project delta cards</strong> replace
            the old &quot;Most important&quot; ticket cards; tickets stay in Blocked / In progress / Done.
          </p>
        </div>

        {/* Temporal truth */}
        <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Icon name="history" size={22} />
            <h3 className="gp-heading3">Temporal truth — how fresh is what you know</h3>
            <SectionTag>Claude Code</SectionTag>
          </div>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            Every fact tracks how old it is and when a human last confirmed it. Search results carry
            <code style={CODE}> age_days</code>, <code style={CODE}>verified_days_ago</code>,
            and <code style={CODE}>verified_by</code>, so your AI can hedge on stale facts instead of
            repeating them confidently (config/architecture/client-notes past 90 days, runbooks past 180; decisions and
            postmortems are history and never go stale).
          </p>
          <p style={{ color: "var(--kt-text-secondary)", fontSize: 14 }}>
            When something is still true, confirm it with <code style={CODE}>verify_thought</code> — a one-click
            re-stamp under your name, so it stops being flagged. The nightly audit turns frequently-used stale facts into a
            standup question; answering it re-verifies the fact automatically. And
            <code style={CODE}> search_thoughts</code> takes an <code style={CODE}>as_of</code> date
            to time-travel — &quot;what did we believe on 2026-01-01?&quot; — surfacing the truth as it stood then, flagged where it
            was later superseded.
          </p>
        </div>

        {/* Account & Data */}
        <div className="gp-section" style={{ gap: 16 }}>
          <h2 className="gp-section-title">Your data</h2>
          <div className="kt-card" style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 14, color: "var(--kt-text-secondary)" }}>
            <p>
              Your thoughts are stored in your Aeronet Brain deployment and isolated to your account —
              other users cannot access your data. You can delete individual thoughts from the
              dashboard at any time.
            </p>
            <p>
              To permanently delete your account and all associated data (thoughts, embeddings, agent
              keys, and integration connections), go to{" "}
              <strong className="gp-strong">Settings → Account → Delete account</strong>. This
              removes everything immediately and cannot be undone.
            </p>
          </div>
        </div>

        {/* CTA footer */}
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 16, borderTop: "1px solid var(--kt-border)", paddingTop: 48 }}>
          <h2 className="gp-section-title">Ready to build your second brain?</h2>
          <p style={{ color: "var(--kt-text-secondary)" }}>Sign in and capture your first thought in under a minute.</p>
          <div>
            <Link to="/login" className="kt-btn kt-btn--primary" style={{ textDecoration: "none", display: "inline-block" }}>
              Get started free
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
